import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	LinearProgress,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	getOrderAvailableCorrectionAmountRequest,
	vatCorrectionSubmitRequest,
} from 'redux/reducers/vatCorrections/reducer';
import {
	getCurrencies,
	getExchangeRate,
	getGbpExchangeRate,
	getOrderAvailableCorrectionAmount,
	getVatCorrectionsSubmitError,
	getVatCorrectionsSubmitLoader,
} from 'redux/reducers/vatCorrections/selectors';
import { TVatCorrectionsSubmitRequestPayload } from 'redux/reducers/vatCorrections/types';
import {
	DeliveryOperator,
	SCHEMES_TO_VAT_CORRECTIONS_SCHEMES,
	VAT_CORRECTIONS_SCHEMES_TO_SCHEMES,
	VAT_CORRECTIONS_SUBMIT_TYPES,
	VatCorrectionsScheme,
} from 'redux/reducers/vatCorrections/constants';
import { CircleSvg } from 'ui/CircleSvg';
import { useParams } from 'react-router';
import { getConfirmedOrdersRequest } from 'redux/reducers/orders/reducer';
import { getConfirmedOrders } from 'redux/reducers/orders/selectors';
import { TNewCorrectionDialogProps } from './types';
import { Step0 } from './Step0';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';

const steps = [
	'Select Order',
	'Select Report',
	'Fill Form',
	'Upload Correction Supportive Document',
	'Submit',
];

export const NewCorrectionDialog: FC<TNewCorrectionDialogProps> = ({ open, setClose }) => {
	const dispatch = useDispatch();
	const params = useParams<{ orderId?: string }>();
	const easOrderIdParam = params?.orderId ?? '';
	const orderAvailableCorrectionAmount = useSelector(getOrderAvailableCorrectionAmount);
	const confirmedOrder = useSelector(getConfirmedOrders);
	const currencies = useSelector(getCurrencies);
	const exchangeRateEUR = useSelector(getExchangeRate);
	const exchangeRateGBP = useSelector(getGbpExchangeRate);
	const vatCorrectionsSubmitIsLoading = useSelector(getVatCorrectionsSubmitLoader);
	const vatCorrectionsSubmitError = useSelector(getVatCorrectionsSubmitError);
	const [easOrderId, setEasOrderId] = useState<number | null>(+easOrderIdParam);
	const [externalOrderId, setExternalOrderId] = useState<string | null>(null);

	const [activeStep, setActiveStep] = useState<number | null>(0);
	const [typeOfReport, setTypeOfReport] = useState<VatCorrectionsScheme | null>(null);
	const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
	const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
	const [amount, setAmount] = useState<number | null>(null);
	const [correctionType, setCorrectionType] = useState<'Add' | 'Deduct' | null>('Deduct');
	const [selectedDate, setSelectedDate] = useState<{
		year: number;
		month?: number;
		quarter?: number;
	} | null>(null);

	const [file, setFile] = useState<File | null>(null);

	const [submitted, setSubmitted] = useState(false);

	const [deliveryOperator, setDeliveryOperator] = useState<DeliveryOperator | null>(null);

	useEffect(() => {
		if (!open) return;
		setActiveStep(0);

		if (easOrderIdParam) {
			setEasOrderId(+easOrderIdParam);
			dispatch(
				getConfirmedOrdersRequest({
					filters: [{ fieldName: 'id', value: easOrderIdParam, operator: '=' }],
				}),
			);
		}
	}, [dispatch, easOrderIdParam, open]);

	useEffect(
		() => {
			if (!open || !easOrderIdParam || !confirmedOrder) return;

			if (easOrderIdParam) {
				setExternalOrderId(confirmedOrder?.data[0]?.externalOrderId ?? null);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[confirmedOrder],
	);

	useEffect(() => {
		if (orderAvailableCorrectionAmount) {
			setSelectedCountry(orderAvailableCorrectionAmount?.country ?? null);
			setSelectedCurrency(orderAvailableCorrectionAmount?.currencyIsoCode ?? null);
		}
	}, [orderAvailableCorrectionAmount]);

	const handleEraseForm = () => {
		setEasOrderId(null);
		setTypeOfReport(null);
		// setExternalOrderId(null);
		setSelectedCountry(null);
		setSelectedCurrency(null);
		setAmount(null);
		setCorrectionType('Deduct');
		setSelectedDate(null);
		setFile(null);
		setDeliveryOperator(null);
	};

	const handleOrderId = (id: number | null) => {
		handleEraseForm();
		setEasOrderId(id);
	};

	const handleSetFile = (f: File | null) => {
		// if (!f) return;
		// const fileTmp = new File([f], 'document.pdf', { type: f?.type });
		setFile(f);
	};

	const handleNext = () => {
		if (activeStep === 0 && externalOrderId && !easOrderId) setExternalOrderId(null);
		if (activeStep === 0 && easOrderId) {
			dispatch(
				getConfirmedOrdersRequest({
					filters: [{ fieldName: 'id', value: easOrderId, operator: '=' }],
				}),
			);
		}
		if (activeStep === 1 && typeOfReport && easOrderId) {
			dispatch(
				getOrderAvailableCorrectionAmountRequest({
					orderId: easOrderId,
					scheme: VAT_CORRECTIONS_SCHEMES_TO_SCHEMES[typeOfReport],
				}),
			);
		}

		setActiveStep((prevActiveStep) => (prevActiveStep ?? 0) + 1);
	};

	const handleTypeOfReportChange = (type: VatCorrectionsScheme) => {
		// handleEraseForm();
		setSelectedCountry(null);
		setSelectedCurrency(null);
		setAmount(null);
		setCorrectionType('Deduct');
		setSelectedDate(null);
		setFile(null);
		setDeliveryOperator(null);

		setTypeOfReport(type);
		if (type === VatCorrectionsScheme.NON_EU_IMPORT) setSelectedCountry('GB');
	};

	const handleClose = () => {
		setActiveStep(null);
		setExternalOrderId(null);
		handleEraseForm();
		setClose();
	};

	const handleSubmit = () => {
		const currencyId = currencies?.find((item) => item.name === selectedCurrency)?.id;
		if (
			!selectedDate?.year ||
			!(typeOfReport === VatCorrectionsScheme.UNION ||
			typeOfReport === VatCorrectionsScheme.NON_UNION
				? selectedDate?.quarter
				: selectedDate?.month) ||
			!currencyId ||
			!selectedCountry ||
			!amount ||
			!correctionType ||
			!file ||
			!typeOfReport
		)
			return;
		const formData = new FormData();
		formData.append('TableName', VAT_CORRECTIONS_SUBMIT_TYPES[typeOfReport]);
		formData.append('Year', selectedDate.year.toString());
		typeOfReport === VatCorrectionsScheme.UNION || typeOfReport === VatCorrectionsScheme.NON_UNION
			? formData.append('Quarter', selectedDate?.quarter?.toString() ?? '1')
			: formData.append('Month', selectedDate?.month?.toString() ?? '1');
		formData.append('Country', selectedCountry);
		// formData.append('currency_id', currency
		formData.append('CurrencyId', typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT ? '3' : '1');
		formData.append(
			'RequestedValueCorrection',
			String(
				typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT
					? (selectedCurrency !== 'GBP' ? (exchangeRateGBP ?? 1) * amount : amount) *
							(correctionType === 'Add' ? 1 : -1)
					: (selectedCurrency !== 'EUR' ? (exchangeRateEUR ?? 1) * amount : amount) *
							(correctionType === 'Add' ? 1 : -1),
			),
		);
		formData.append('File', file, file.name);
		// formData.append('ActionRemarks', '');
		formData.append('DeliveryOperator', deliveryOperator ?? '');
		formData.append('OrderId', easOrderId?.toString() ?? '');

		dispatch(
			vatCorrectionSubmitRequest(formData as unknown as TVatCorrectionsSubmitRequestPayload),
		);
		setSubmitted(true);
		// handleClose();
	};

	useEffect(() => {
		if (submitted && !vatCorrectionsSubmitIsLoading && !vatCorrectionsSubmitError) {
			setClose();
			setActiveStep(0);
			handleEraseForm();
			setSubmitted(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vatCorrectionsSubmitIsLoading]);

	useEffect(() => {
		if (activeStep === 1 && confirmedOrder?.data[0]?.schemes && !!easOrderId) {
			const schemes = confirmedOrder?.data[0]?.schemes;
			const schemesArray = schemes?.split(',');
			if (schemesArray?.length === 1) {
				handleTypeOfReportChange(
					SCHEMES_TO_VAT_CORRECTIONS_SCHEMES[
						schemesArray[0] as keyof typeof SCHEMES_TO_VAT_CORRECTIONS_SCHEMES
					],
				);
			} else {
				setTypeOfReport(null);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStep, confirmedOrder]);

	const handleCancel = () => {
		handleEraseForm();
		setClose();
		if (easOrderIdParam)
			dispatch(
				getConfirmedOrdersRequest({
					filters: [{ fieldName: 'id', value: easOrderIdParam, operator: '=' }],
				}),
			);
	};

	return (
		<Dialog
			open={open}
			// onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			sx={{
				'.MuiDialog-paper': {
					width: '100%',
					maxWidth: '920px',
					minHeight: '575px',
					borderRadius: '12px',
					margin: { xs: 0, sm: 'auto' },
				},
			}}
		>
			<DialogTitle color="primary">Create New VAT Correction</DialogTitle>
			<DialogContent>
				<>
					<Box
						sx={{
							margin: '10px 0 20px 0',
							p: 0,
							display: { xs: 'none', sm: 'flex' },
							// width: '800px',
							// overflowX: 'hidden',
						}}
					>
						<Stepper
							activeStep={activeStep ?? 0}
							sx={{
								p: 0,
								m: 0,
							}}
						>
							{steps.map((label, index) => {
								const stepProps: { completed?: boolean } = {};
								const labelProps: {
									optional?: React.ReactNode;
								} = {};
								return (
									<Step key={label} {...stepProps}>
										<StepLabel {...labelProps}>{label}</StepLabel>
									</Step>
								);
							})}
						</Stepper>
					</Box>
					<Box sx={{ display: { sm: 'none', xs: 'flex' }, gap: 1, m: '8px 0px 16px 0px' }}>
						<CircleSvg num={(activeStep ?? 0) + 1} active />{' '}
						<Typography
							sx={{
								color: 'rgba(0, 0, 0, 0.87)',
								fontWeight: '500',
								fontSize: '0.875rem',
							}}
						>
							{steps[activeStep ?? 0]}
						</Typography>
					</Box>
					{activeStep === 0 && (
						<Step0
							easOrderId={easOrderId}
							setEasOrderId={handleOrderId}
							externalOrderId={externalOrderId}
							setExternalOrderId={setExternalOrderId}
						/>
					)}
					{activeStep === 1 && (
						<Step1
							typeOfReport={typeOfReport}
							handleTypeOfReportChange={handleTypeOfReportChange}
							hasSelectedOrder={!!easOrderId}
						/>
					)}
					{activeStep === 2 && typeOfReport && (
						<Step2
							typeOfReport={typeOfReport}
							selectedCountry={selectedCountry}
							setSelectedCountry={setSelectedCountry}
							selectedCurrency={selectedCurrency}
							setSelectedCurrency={setSelectedCurrency}
							correctionType={correctionType}
							setCorrectionType={setCorrectionType}
							setAmount={setAmount}
							amount={amount}
							selectedDate={selectedDate}
							setSelectedDate={setSelectedDate}
							deliveryOperator={deliveryOperator}
							setDeliveryOperator={setDeliveryOperator}
							hasSelectedOrder={!!easOrderId}
						/>
					)}
					{activeStep === 3 && (
						<Step3
							name={file?.name ?? null}
							size={file?.size ?? null}
							onFileChange={handleSetFile}
						/>
					)}
					{activeStep === 4 && typeOfReport && (
						<Step4
							externalOrderId={externalOrderId}
							fileName={file?.name ?? null}
							fileSize={file?.size ?? null}
							typeOfReport={typeOfReport}
							selectedCountry={selectedCountry}
							selectedCurrency={selectedCurrency}
							selectedDate={selectedDate}
							correctionType={correctionType}
							amount={amount}
							deliveryOperator={deliveryOperator}
						/>
					)}
					{vatCorrectionsSubmitIsLoading && (
						<Box sx={{ width: '100%', mt: '10px' }}>
							<LinearProgress />
						</Box>
					)}
					{submitted && !vatCorrectionsSubmitIsLoading && vatCorrectionsSubmitError && (
						<Box sx={{ mt: '10px', color: 'red' }}>{vatCorrectionsSubmitError}</Box>
					)}
				</>
			</DialogContent>

			<DialogActions>
				{(activeStep ?? 0) < steps.length && (
					<Button onClick={handleCancel} disabled={vatCorrectionsSubmitIsLoading}>
						Cancel
					</Button>
				)}
				{activeStep !== 0 && (activeStep ?? 0) < steps.length && (
					<Button
						onClick={() => {
							setSubmitted(false);
							setActiveStep((prevActiveStep) => (prevActiveStep ?? 0) - 1);
						}}
						disabled={vatCorrectionsSubmitIsLoading}
					>
						Back
					</Button>
				)}
				{activeStep === steps.length - 1 ? (
					<Button onClick={handleSubmit} disabled={vatCorrectionsSubmitIsLoading || submitted}>
						Submit
					</Button>
				) : (
					<Button
						onClick={handleNext}
						autoFocus
						disabled={
							(activeStep === 1 && !typeOfReport) ||
							(activeStep === 2 &&
								!(
									!!selectedCountry &&
									!!selectedCurrency &&
									!!correctionType &&
									!!amount &&
									!!selectedDate &&
									((typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT &&
										selectedCurrency === 'GBP') ||
										exchangeRateGBP ||
										(typeOfReport !== VatCorrectionsScheme.NON_EU_IMPORT &&
											selectedCurrency === 'EUR') ||
										exchangeRateEUR)
								)) ||
							(activeStep === 3 && !file) ||
							(file?.size ?? 0) > 10_000_000
						}
					>
						Next
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
